import { useEffect, useState } from "react";
import type { EnablerGroup, Objective } from "../types";
import { ChevronIcon } from "./Icons";
import { HighlightedText } from "./HighlightedText";
import { ResourceCard } from "./ResourceCard";

type EnablerCardProps = {
  enabler: EnablerGroup;
  objectivesById: Map<string, Objective>;
  defaultOpen?: boolean;
  searchTerm?: string;
};

export function EnablerCard({ enabler, objectivesById, defaultOpen = false, searchTerm = "" }: EnablerCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen, enabler.code]);

  const panelId = `enabler-${enabler.code}-resources`;

  return (
    <article className="objective-card enabler-card">
      <button
        className="objective-card__button"
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="objective-card__copy">
          <span className="objective-card__meta">
            <span>{enabler.theme}</span>
            <span className="objective-card__code">/ {enabler.code}</span>
          </span>
          <span className="objective-card__title">
            <HighlightedText text={enabler.title} searchTerm={searchTerm} />
          </span>
          <span className="objective-card__description">
            <HighlightedText text={enabler.definition} searchTerm={searchTerm} />
          </span>
          <span className="objective-card__count">
            {enabler.resources.length} resource{enabler.resources.length === 1 ? "" : "s"}
          </span>
        </span>
        <ChevronIcon className={isOpen ? "objective-card__chevron objective-card__chevron--open" : "objective-card__chevron"} />
      </button>

      <div className={isOpen ? "objective-card__panel objective-card__panel--open" : "objective-card__panel"} id={panelId}>
        <div className="objective-card__resources">
          {enabler.resources.length > 0 ? (
            enabler.resources.map((resource, index) => (
              <ResourceCard
                key={`${resource.url}-${index}`}
                resource={resource}
                objectives={resource.objectiveIds.flatMap((id) => objectivesById.get(id) ?? [])}
                searchTerm={searchTerm}
              />
            ))
          ) : (
            <p className="objective-card__empty">No resources in this section.</p>
          )}
        </div>
      </div>
    </article>
  );
}
